import React, { useState, useEffect } from 'react';
import { dashboardService } from '../../services/dashboardService';
import { usePermissions } from '../../hooks/usePermission';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './DashboardHome.module.css';
import FormModal from '../../components/forms/FormModal';
import { getTimeOffTypes, createTimeOffRequest } from '../../services/timeOffService';
import * as peopleService from '../../services/peopleService';
import CustomDropdown from '../../components/forms/CustomDropdown';
import { useAuth } from '../../context/AuthContext';
import useMinimumLoader from '../../hooks/useMinimumLoader';

export default function DashboardHome() {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { user } = useAuth();
  const { 
    canViewProjects, 
    canCreateProjects, 
    canViewSchedule, 
    canCreateBookings,
    canViewPeople,
    canCreateTimesheets,
    canCreateOwnTimeOff,
    canApproveTimeOff
  } = usePermissions();

  // State for all dashboard data
  const [snapshot, setSnapshot] = useState(null);
  const [heatmap, setHeatmap] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [projectOverview, setProjectOverview] = useState([]);
  const [teamAvailability, setTeamAvailability] = useState([]);
  const [personalData, setPersonalData] = useState(null);
  
  // Time off modal state
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [timeOffForm, setTimeOffForm] = useState({
    employee_id: '',
    time_off_type_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [, setTimeOffLoading] = useState(false);
  const [timeOffError, setTimeOffError] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(canViewProjects ? 'manager' : 'personal');
  const [heatmapDays, setHeatmapDays] = useState(7);
  const [heatmapPage, setHeatmapPage] = useState(0);
  const [overviewMetric, setOverviewMetric] = useState('time');

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, heatmapDays]);

  useEffect(() => {
    setHeatmapPage(0);
  }, [heatmapDays, viewMode]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (viewMode === 'manager' && canViewProjects) {
        // Load manager view data
        const [snapshotData, heatmapData, alertsData, overviewData, availabilityData] = await Promise.all([
          dashboardService.getExecutiveSnapshot().catch(() => null),
          dashboardService.getResourceHeatmap(heatmapDays).catch(() => []),
          dashboardService.getAttentionRequired().catch(() => []),
          dashboardService.getProjectStatusOverview().catch(() => []),
          dashboardService.getTeamAvailability().catch(() => [])
        ]);

        setSnapshot(snapshotData);
        setHeatmap(heatmapData);
        setAlerts(alertsData);
        setProjectOverview(overviewData);
        setTeamAvailability(availabilityData);
      } else {
        // Load personal view data
        const personalDashboard = await dashboardService.getPersonalDashboard();
        console.log(personalDashboard);
        setPersonalData(personalDashboard);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return '↑';
    if (trend < 0) return '↓';
    return '→';
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const hasBudget = (budgetValue) => {
    if (budgetValue === null || budgetValue === undefined || budgetValue === '') return false;
    const parsed = Number(budgetValue);
    return Number.isFinite(parsed) && parsed > 0;
  };

  const getBudgetPercent = (actual, budget) => {
    const budgetNum = toNumber(budget);
    if (budgetNum <= 0) return null;
    return (toNumber(actual) / budgetNum) * 100;
  };

  const getBudgetFillClass = (percent) => {
    if (percent === null) return styles.budgetFillUnknown;
    if (percent > 100) return styles.budgetFillOver;
    if (percent >= 85) return styles.budgetFillNear;
    return styles.budgetFillGood;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(toNumber(value));
  };

  const formatHours = (value) => `${toNumber(value).toFixed(1)}h`;

  const getMetricConfig = (project) => {
    if (overviewMetric === 'cost') {
      return {
        label: 'Cost',
        budgetValue: project.budget_cost,
        scheduledValue: project.scheduled_cost,
        actualValue: project.actual_cost,
        formatter: formatCurrency,
        noBudgetMessage: 'No cost budget set'
      };
    }

    return {
      label: 'Time',
      budgetValue: project.budget_hours,
      scheduledValue: project.scheduled_hours,
      actualValue: project.actual_hours,
      formatter: formatHours,
      noBudgetMessage: 'No time budget set'
    };
  };

  // Time off functions
  const openTimeOffModal = async () => {
    setTimeOffError(null);

    try {
      const types = await getTimeOffTypes();
      setTimeOffTypes(types);

      setTimeOffForm({
        employee_id: user.user_id,
        time_off_type_id: String(types.length > 0 ? types[0].leave_type_id : ''),
        start_date: '',
        end_date: '',
        reason: '',
        status: canApproveTimeOff ? 'approved' : 'pending'
      });

      if (canApproveTimeOff) {
        const employeeData = await peopleService.getEmployees();
        setEmployees(employeeData.employees || employeeData);
      }

      setShowTimeOffModal(true);
    } catch (error) {
      console.error('Error loading time off data:', error);
      setTimeOffError('Failed to load time off data');
    }
  };

  useEffect(() => {
    if (!routeLocation.state?.openTimeOffModal) return;
    if (!canCreateOwnTimeOff && !canApproveTimeOff) return;

    openTimeOffModal();
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation.state, canCreateOwnTimeOff, canApproveTimeOff]);

  const handleTimeOffSubmit = async (e) => {
    e.preventDefault();
    setTimeOffLoading(true);
    setTimeOffError(null);

    try {
      await createTimeOffRequest(timeOffForm);
      setShowTimeOffModal(false);
      setTimeOffForm({
        time_off_type_id: String(timeOffTypes.length > 0 ? timeOffTypes[0].leave_type_id : ''),
        start_date: '',
        end_date: '',
        reason: '',
        status: canApproveTimeOff ? 'approved' : 'pending'
      });
      // Could add a success toast here
    } catch (error) {
      console.error('Error creating time off request:', error);
      setTimeOffError(error.response?.data?.error || 'Failed to create time off request');
    } finally {
      setTimeOffLoading(false);
    }
  };

  const getTimeOffButtonText = () => {
    if (canApproveTimeOff) {
      return '+ Schedule Leave';
    } else if (canCreateOwnTimeOff) {
      return '+ Request Leave';
    }
    return null;
  };

  const getUtilizationColor = (status) => {
    switch (status) {
      case 'available': return styles.utilGreen;
      case 'underutilized': return styles.utilLightGreen;
      case 'optimal': return styles.utilYellow;
      case 'overloaded': return styles.utilRed;
      default: return styles.utilGray;
    }
  };

  const getUtilizationLabel = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'underutilized': return 'Underutilized';
      case 'optimal': return 'Optimal';
      case 'overloaded': return 'Overloaded';
      default: return 'Unknown';
    }
  };

  const getInitials = (name = '') => {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'NA';
  };

  const formatHeatmapHeaderDate = (dateString) => {
    if (!dateString) {
      return { weekday: '-', day: '-' };
    }

    let date;
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map((part) => parseInt(part, 10));
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }

    if (Number.isNaN(date.getTime())) {
      return { weekday: '-', day: '-' };
    }

    return {
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
      day: date.toLocaleDateString('en-US', { day: '2-digit' })
    };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return styles.priorityHigh;
      case 'medium': return styles.priorityMedium;
      case 'low': return styles.priorityLow;
      default: return styles.priorityMedium;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    let date;

    // Parse date-only values as local calendar dates to avoid UTC day shifting.
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map((part) => parseInt(part, 10));
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateKey = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return getLocalDateKey(parsed);
      }

      return value.slice(0, 10);
    }
    if (value instanceof Date) {
      return getLocalDateKey(value);
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return getLocalDateKey(parsed);
    }
    return String(value).slice(0, 10);
  };

  const dateKeyToDate = (dateKey) => {
    const [year, month, day] = dateKey.split('-').map((part) => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };

  // Group heatmap by employee
  const groupedHeatmap = heatmap.reduce((acc, row) => {
    if (!acc[row.employee_id]) {
      acc[row.employee_id] = {
        employee_name: row.employee_name,
        job_title: row.job_title,
        days: []
      };
    }
    acc[row.employee_id].days.push(row);
    return acc;
  }, {});

  const HEATMAP_PAGE_SIZE = 5;
  const heatmapEntries = Object.entries(groupedHeatmap);
  const totalHeatmapPages = Math.max(1, Math.ceil(heatmapEntries.length / HEATMAP_PAGE_SIZE));
  const clampedHeatmapPage = Math.min(Math.max(heatmapPage, 0), totalHeatmapPages - 1);
  const heatmapStartIndex = clampedHeatmapPage * HEATMAP_PAGE_SIZE;
  const paginatedHeatmapEntries = heatmapEntries.slice(
    heatmapStartIndex,
    heatmapStartIndex + HEATMAP_PAGE_SIZE
  );

  useEffect(() => {
    if (heatmapPage > totalHeatmapPages - 1) {
      setHeatmapPage(Math.max(totalHeatmapPages - 1, 0));
    }
  }, [heatmapPage, totalHeatmapPages]);

  const groupedPersonalSchedule = (personalData?.next14Schedule || []).reduce((acc, booking) => {
    const dateKey = getDateKey(booking.work_date);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(booking);
    return acc;
  }, {});

  const next14CalendarDays = Array.from({ length: 14 }, (_, idx) => {
    const dateObj = new Date();
    dateObj.setHours(0, 0, 0, 0);
    dateObj.setDate(dateObj.getDate() + idx);
    const dateKey = getLocalDateKey(dateObj);

    return {
      dateKey,
      dateObj,
      bookings: groupedPersonalSchedule[dateKey] || []
    };
  });

  const thisWeekTimesheets = personalData?.thisWeekTimesheets || [];
  const thisWeekTimesheetHours = thisWeekTimesheets.reduce(
    (sum, entry) => sum + (Number(entry.hours_worked) || 0),
    0
  );

  const groupedWeekTimesheets = thisWeekTimesheets.reduce((acc, entry) => {
    const dateKey = getDateKey(entry.work_date);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(entry);
    return acc;
  }, {});

  const workloadWeekDays = (() => {
    const explicitWeekStart = personalData?.workload?.week_start
      ? getDateKey(personalData.workload.week_start)
      : null;

    return Array.from({ length: 7 }, (_, idx) => {
      let dateObj;

      if (explicitWeekStart) {
        dateObj = dateKeyToDate(explicitWeekStart);
        dateObj.setDate(dateObj.getDate() + idx);
      } else {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const mondayOffset = (dayOfWeek + 6) % 7;
        dateObj = new Date(today);
        dateObj.setHours(0, 0, 0, 0);
        dateObj.setDate(today.getDate() - mondayOffset + idx);
      }

      const dateKey = getLocalDateKey(dateObj);
      return {
        dateKey,
        dateObj,
        entries: groupedWeekTimesheets[dateKey] || []
      };
    });
  })();

  const weeklyCapacity = toNumber(personalData?.workload?.weekly_hours);
  const scheduledHours = toNumber(personalData?.workload?.booked_hours);
  const loggedHours = toNumber(thisWeekTimesheetHours);
  const scheduledWorkloadPercent = weeklyCapacity > 0 ? (scheduledHours / weeklyCapacity) * 100 : 0;
  const loggedWorkloadPercent = weeklyCapacity > 0 ? (loggedHours / weeklyCapacity) * 100 : 0;
  const leaveCounts = personalData?.leaveCounts || {
    total_available_days: 0,
    used_days: 0,
    pending_days: 0,
    remaining_days: 0
  };
  const leaveByType = personalData?.leaveByType || [];

  const renderManagerSkeleton = () => (
    <>
      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={styles.snapshotGrid}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={`manager-metric-${idx}`} className={`${styles.metricCard} ${styles.skeletonMetricCard}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineLg}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonControl}`} />
        </div>
        <div className={`${styles.skeletonBlock} ${styles.skeletonLegend}`} />
        <div className={styles.skeletonTableWrap}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonTableHeader}`} />
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={`heatmap-row-${idx}`} className={`${styles.skeletonBlock} ${styles.skeletonTableRow}`} />
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonToggle}`} />
        </div>
        <div className={styles.statusGrid}>
          {Array.from({ length: 3 }).map((_, statusIdx) => (
            <div key={`status-col-${statusIdx}`} className={`${styles.statusColumn} ${styles.skeletonColumn}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
              <div className={styles.projectList}>
                {Array.from({ length: 2 }).map((__, cardIdx) => (
                  <div key={`status-card-${statusIdx}-${cardIdx}`} className={`${styles.projectCard} ${styles.skeletonProjectCard}`}>
                    <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
                    <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
                    <div className={`${styles.skeletonBlock} ${styles.skeletonBar}`} />
                    <div className={`${styles.skeletonBlock} ${styles.skeletonBar}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={styles.teamGrid}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={`team-skeleton-${idx}`} className={`${styles.teamCard} ${styles.skeletonTeamCard}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonAvatar}`} />
              <div className={styles.teamInfo}>
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineXs}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={styles.alertsList}>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={`alert-skeleton-${idx}`} className={`${styles.alertItem} ${styles.skeletonAlertItem}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonIcon}`} />
              <div className={styles.alertContent}>
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineLg}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const renderPersonalSkeleton = () => (
    <>
      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={`${styles.workloadCard} ${styles.skeletonSurface}`}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonBar}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonBar}`} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={styles.leaveStatsGrid}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={`leave-skeleton-${idx}`} className={`${styles.leaveStatCard} ${styles.skeletonSurface}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={styles.miniCalendarGrid}>
          {Array.from({ length: 7 }).map((_, idx) => (
            <div key={`calendar-skeleton-${idx}`} className={`${styles.miniCalendarDay} ${styles.skeletonSurface}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonHeading}`} />
        <div className={styles.deadlinesList}>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={`deadline-skeleton-${idx}`} className={`${styles.deadlineItem} ${styles.skeletonSurface}`}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonDate}`} />
              <div className={styles.deadlineContent}>
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineMd}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonLineSm}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Error loading dashboard: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header with view toggle */}
      <div className={styles.header}>
        <h1>Dashboard</h1>
        {canViewProjects && (
          <div className={styles.viewToggle}>
            <button 
              className={viewMode === 'manager' ? styles.active : ''}
              onClick={() => setViewMode('manager')}
            >
              Manager View
            </button>
            <button 
              className={viewMode === 'personal' ? styles.active : ''}
              onClick={() => setViewMode('personal')}
            >
              Personal View
            </button>
          </div>
        )}
      </div>

      {/* Manager View */}
      {viewMode === 'manager' && canViewProjects && (
        <>
          {showLoading ? (
            renderManagerSkeleton()
          ) : (
            <div className={styles.fadeInContent}>
          {/* 1. Executive Snapshot */}
          {snapshot && (
            <div className={styles.section}>
              <h2>Executive Snapshot</h2>
              <div className={styles.snapshotGrid}>
                <div className={`${styles.metricCard} ${styles.metricCardPrimary}`}>
                  <div className={styles.metricLabel}>Active Projects</div>
                  <div className={styles.metricValue}>
                    {snapshot.activeProjects.count}
                  </div>
                  <span className={styles.metricBadge}>
                    <span className={styles.trend}>
                      {getTrendIcon(snapshot.activeProjects.trend)} {snapshot.activeProjects.trend}
                    </span>
                    this week
                  </span>
                </div>

                <div className={`${styles.metricCard} ${styles.metricCardNeutral}`}>
                  <div className={styles.metricLabel}>People Utilization</div>
                  <div className={styles.metricValue}>
                    {snapshot.utilization.percent.toFixed(1)}%
                  </div>
                  <div className={styles.metricProgressTrack}>
                    <div
                      className={styles.metricProgressFill}
                      style={{ width: `${Math.max(0, Math.min(snapshot.utilization.percent, 100))}%` }}
                    />
                  </div>
                  <div className={styles.metricSubtext}>Average capacity booked</div>
                </div>

                <div className={`${styles.metricCard} ${styles.metricCardNeutral}`}>
                  <div className={styles.metricLabel}>Overallocated Staff</div>
                  <div className={styles.metricValue}>
                    <span className={snapshot.overallocatedStaff.count > 0 ? styles.alertText : ''}>
                      {snapshot.overallocatedStaff.count}
                    </span>
                  </div>
                  <div className={styles.metricSubtext}>Over capacity this week</div>
                </div>

                <div className={`${styles.metricCard} ${styles.metricCardNeutral}`}>
                  <div className={styles.metricLabel}>Upcoming Deadlines</div>
                  <div className={styles.metricValue}>{snapshot.upcomingDeadlines.count}</div>
                  <div className={styles.metricSubtext}>
                    {snapshot.upcomingDeadlines.mostUrgentProject || 'Next 7 days'}
                  </div>
                </div>

                <div className={`${styles.metricCard} ${styles.metricCardAccent}`}>
                  <div className={styles.metricLabel}>Open Leads</div>
                  <div className={styles.metricValue}>{snapshot.openLeads?.count || 0}</div>
                  <div className={styles.metricSubtext}>In pipeline</div>
                </div>
              </div>
            </div>
          )}

          {/* 2. Resource Heatmap */}
          {canViewSchedule && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Resource Timeline</h2>
                <div className={styles.heatmapControls}>
                  <CustomDropdown
                    toolbar
                    value={String(heatmapDays)}
                    onChange={(val) => setHeatmapDays(parseInt(val))}
                    options={[
                      { value: '7', label: '7 days' },
                      { value: '14', label: '14 days' }
                    ]}
                  />
                </div>
              </div>
              
              <div className={styles.heatmapContainer}>
                <div className={styles.heatmapLegend}>
                  <span className={`${styles.legendPill} ${styles.utilGreen}`}>Available</span>
                  <span className={`${styles.legendPill} ${styles.utilLightGreen}`}>Underutilized</span>
                  <span className={`${styles.legendPill} ${styles.utilYellow}`}>Optimal</span>
                  <span className={`${styles.legendPill} ${styles.utilRed}`}>Overloaded</span>
                </div>

                {heatmapEntries.length > 0 && (
                  <div className={styles.heatmapPager}>
                    <div className={styles.heatmapPagerMeta}>
                      Showing {heatmapStartIndex + 1}-{Math.min(heatmapStartIndex + HEATMAP_PAGE_SIZE, heatmapEntries.length)} of {heatmapEntries.length}
                    </div>
                    <div className={styles.heatmapPagerControls}>
                      <button
                        type="button"
                        className={styles.heatmapPagerButton}
                        onClick={() => setHeatmapPage((prev) => Math.max(prev - 1, 0))}
                        disabled={clampedHeatmapPage === 0}
                      >
                        Previous
                      </button>
                      <span className={styles.heatmapPagerPage}>Page {clampedHeatmapPage + 1} / {totalHeatmapPages}</span>
                      <button
                        type="button"
                        className={styles.heatmapPagerButton}
                        onClick={() => setHeatmapPage((prev) => Math.min(prev + 1, totalHeatmapPages - 1))}
                        disabled={clampedHeatmapPage >= totalHeatmapPages - 1}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {heatmapEntries.length > 0 ? (
                  <div className={styles.heatmap}>
                    <table className={styles.heatmapTable}>
                      <thead>
                        <tr>
                          <th className={styles.nameColumn}>Employee</th>
                          {paginatedHeatmapEntries[0]?.[1]?.days.map((day, idx) => {
                            const headerDate = formatHeatmapHeaderDate(day.work_date);
                            return (
                              <th key={idx}>
                                <div className={styles.dayHeaderWrap}>
                                  <span className={styles.dayHeaderWeekday}>{headerDate.weekday}</span>
                                  <span className={styles.dayHeaderDate}>{headerDate.day}</span>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedHeatmapEntries.map(([employeeId, data]) => (
                          <tr key={employeeId}>
                            <td className={styles.nameColumn}>
                              <div className={styles.employeeCell}>
                                <div className={styles.employeeAvatar}>{getInitials(data.employee_name)}</div>
                                <div className={styles.employeeMeta}>
                                  <div className={styles.employeeName}>{data.employee_name}</div>
                                  <div className={styles.employeeRole}>{data.job_title || 'Team member'}</div>
                                </div>
                              </div>
                            </td>
                            {data.days.map((day, idx) => (
                              <td 
                                key={idx} 
                                className={`${styles.heatmapCell} ${getUtilizationColor(day.status)}`}
                                title={`${parseFloat(day.booked_hours).toFixed(1)}h / ${parseFloat(day.daily_capacity).toFixed(1)}h (${day.utilization_percent}%)`}
                              >
                                <span className={styles.heatmapStatus}>{getUtilizationLabel(day.status)}</span>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={styles.emptyState}>No resource data available</div>
                )}
              </div>
            </div>
          )}

          {/* 3. Project Status Overview */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Project Status Overview</h2>
              <div className={styles.metricToggle}>
                <button
                  type="button"
                  className={overviewMetric === 'time' ? styles.metricToggleActive : ''}
                  onClick={() => setOverviewMetric('time')}
                >
                  Time
                </button>
                <button
                  type="button"
                  className={overviewMetric === 'cost' ? styles.metricToggleActive : ''}
                  onClick={() => setOverviewMetric('cost')}
                >
                  Cost
                </button>
              </div>
            </div>
            <div className={styles.statusGrid}>
              {projectOverview.map((status) => (
                <div key={status.status_id || status.status_name} className={styles.statusColumn}>
                  <div className={styles.statusHeader}>
                    <h3>{status.status_name}</h3>
                    <span className={styles.statusCount}>{status.project_count}</span>
                  </div>
                  <div className={styles.projectList}>
                    {status.projects && status.projects.length > 0 ? (
                      status.projects.map((project) => (
                        (() => {
                          const metric = getMetricConfig(project);
                          const hasSelectedBudget = hasBudget(metric.budgetValue);
                          const scheduledPercent = getBudgetPercent(metric.scheduledValue, metric.budgetValue);
                          const actualPercent = getBudgetPercent(metric.actualValue, metric.budgetValue);
                          const isOverdue = Boolean(project.is_overdue);

                          return (
                            <div
                              key={project.project_id}
                              className={`${styles.projectCard} ${styles.projectOverviewCard} ${isOverdue ? styles.projectCardOverdue : ''}`}
                              onClick={() => navigate('/dashboard/projects', { state: { openProjectDetailModal: project.project_id } })}
                            >
                              <div className={styles.projectName}>{project.client_company} - {project.project_name || "N/A"}</div>
                              <div className={styles.projectMeta}>
                                <span className={styles.urgencyBadge}>{project.urgency}</span>
                                {isOverdue && <span className={styles.overdueBadge}>Overdue</span>}
                              </div>

                              {isOverdue && project.end_date && (
                                <div className={styles.overdueDueDate}>
                                  Due: {formatDate(project.end_date)}
                                </div>
                              )}

                              <div className={styles.budgetSection}>
                                <div className={styles.budgetItem}>
                                  <div className={styles.budgetRowHeader}>
                                    <span className={styles.budgetLabel}>Scheduled {metric.label}</span>
                                    {hasSelectedBudget ? (
                                      <span className={styles.budgetNumbers}>
                                        {metric.formatter(metric.scheduledValue)} / {metric.formatter(metric.budgetValue)}
                                      </span>
                                    ) : (
                                      <span className={styles.budgetNumbers}>
                                        {metric.formatter(metric.scheduledValue)}
                                      </span>
                                    )}
                                  </div>
                                  {hasSelectedBudget && (
                                    <div className={styles.budgetBarTrack}>
                                      <div
                                        className={`${styles.budgetBarFill} ${getBudgetFillClass(scheduledPercent)}`}
                                        style={{ width: `${Math.min(scheduledPercent, 100)}%` }}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className={styles.budgetItem}>
                                  <div className={styles.budgetRowHeader}>
                                    <span className={styles.budgetLabel}>Actual {metric.label}</span>
                                    {hasSelectedBudget ? (
                                      <span className={styles.budgetNumbers}>
                                        {metric.formatter(metric.actualValue)} / {metric.formatter(metric.budgetValue)}
                                      </span>
                                    ) : (
                                      <span className={styles.budgetNumbers}>
                                        {metric.formatter(metric.actualValue)}
                                      </span>
                                    )}
                                  </div>
                                  {hasSelectedBudget && (
                                    <div className={styles.budgetBarTrack}>
                                      <div
                                        className={`${styles.budgetBarFill} ${getBudgetFillClass(actualPercent)}`}
                                        style={{ width: `${Math.min(actualPercent, 100)}%` }}
                                      />
                                    </div>
                                  )}
                                </div>

                                {!hasSelectedBudget && (
                                  <div className={styles.noBudgetText}>{metric.noBudgetMessage}</div>
                                )}
                              </div>
                            </div>
                          );
                        })()
                      ))
                    ) : (
                      <div className={styles.emptyState}>No projects</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4. Team Availability Snapshot */}
          {canViewPeople && (
            <div className={styles.section}>
              <h2>Team Availability Today</h2>
              <div className={styles.teamGrid}>
                {teamAvailability.map((member) => (
                  <div key={member.user_id} className={styles.teamCard}>
                    <div className={styles.avatar}>
                      {member.avatar_url ? (
                        <img src={member.avatar_url} alt={member.name} />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className={styles.teamInfo}>
                      <div className={styles.teamName}>{member.name}</div>
                      <div className={styles.teamTitle}>{member.job_title}</div>
                      <div className={`${styles.availabilityBadge} ${styles[member.availability_status]}`}>
                        {member.availability_status.replace('_', ' ')}
                      </div>
                      {member.availability_status !== 'on_leave' && (
                        <div className={styles.teamHours}>
                          {parseFloat(member.booked_hours).toFixed(1)}h / {parseFloat(member.daily_capacity).toFixed(1)}h
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {teamAvailability.length === 0 && (
                  <div className={styles.emptyState}>No team members found</div>
                )}
              </div>
            </div>
          )}

          {/* 5. Attention Required */}
          <div className={styles.section}>
            <h2>Attention Required</h2>
            {alerts.length > 0 ? (
              <div className={styles.alertsList}>
                {alerts.map((alert, idx) => (
                  <div key={idx} className={`${styles.alertItem} ${getPriorityColor(alert.priority)}`}>
                    <div className={styles.alertIcon}>⚠</div>
                    <div className={styles.alertContent}>
                      <div className={styles.alertTitle}>
                        {alert.name || (alert.client_company + " - " + (alert.project_name || 'N/A')) || `${alert.type} #${alert.id}`}
                      </div>
                      <div className={styles.alertMessage}>{alert.issue}</div>
                      {alert.due_date && (
                        <div className={styles.alertMeta}>Due: {formatDate(alert.due_date)}</div>
                      )}
                      {alert.booked_hours && (
                        <div className={styles.alertMeta}>
                          {alert.booked_hours}h / {alert.weekly_hours}h
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>✓ No immediate attention required</div>
            )}
          </div>
            </div>
          )}
        </>
      )}

      {/* Personal View */}
      {viewMode === 'personal' && (
        <>
          {showLoading ? (
            renderPersonalSkeleton()
          ) : (
            personalData && (
              <div className={styles.fadeInContent}>
          {/* Personal Workload */}
          <div className={styles.section}>
            <h2>My Workload This Week</h2>
            {personalData.workload.week_start && personalData.workload.week_end && (
              <div className={styles.workloadRange}>
                Week: {formatDate(personalData.workload.week_start)} - {formatDate(personalData.workload.week_end)}
              </div>
            )}
            <div className={styles.workloadCard}>
              <div className={styles.workloadMetric}>
                <span className={styles.workloadLabel}>Weekly Capacity</span>
                <span className={styles.workloadValue}>{weeklyCapacity.toFixed(1)}h</span>
              </div>

              <div className={styles.workloadBars}>
                <div className={styles.workloadBarRow}>
                  <div className={styles.workloadBarHeader}>
                    <span className={styles.workloadBarLabel}>Scheduled / Booked</span>
                    <span className={styles.workloadBarValue}>
                      {scheduledHours.toFixed(1)}h / {weeklyCapacity.toFixed(1)}h ({scheduledWorkloadPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className={styles.workloadBarTrack}>
                    <div
                      className={`${styles.workloadBarFill} ${styles.workloadBarBooked}`}
                      style={{
                        width: `${Math.min(scheduledWorkloadPercent, 100)}%`,
                        backgroundColor: scheduledWorkloadPercent > 100 ? '#b45353' : undefined
                      }}
                    />
                  </div>
                </div>

                <div className={styles.workloadBarRow}>
                  <div className={styles.workloadBarHeader}>
                    <span className={styles.workloadBarLabel}>Logged</span>
                    <span className={styles.workloadBarValue}>
                      {loggedHours.toFixed(1)}h / {weeklyCapacity.toFixed(1)}h ({loggedWorkloadPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className={styles.workloadBarTrack}>
                    <div
                      className={`${styles.workloadBarFill} ${styles.workloadBarLogged}`}
                      style={{
                        width: `${Math.min(loggedWorkloadPercent, 100)}%`,
                        backgroundColor: loggedWorkloadPercent > 100 ? '#b45353' : undefined
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Leave Counts */}
          <div className={styles.section}>
            <h2>My Leave</h2>
            <div className={styles.leaveStatsGrid}>
              <div className={styles.leaveStatCard}>
                <div className={styles.leaveStatLabel}>Available</div>
                <div className={styles.leaveStatValue}>{Number(leaveCounts.total_available_days || 0).toFixed(1)}</div>
              </div>
              <div className={styles.leaveStatCard}>
                <div className={styles.leaveStatLabel}>Used</div>
                <div className={styles.leaveStatValue}>{Number(leaveCounts.used_days || 0).toFixed(1)}</div>
              </div>
              <div className={styles.leaveStatCard}>
                <div className={styles.leaveStatLabel}>Pending</div>
                <div className={styles.leaveStatValue}>{Number(leaveCounts.pending_days || 0).toFixed(1)}</div>
              </div>
              <div className={styles.leaveStatCard}>
                <div className={styles.leaveStatLabel}>Remaining</div>
                <div className={styles.leaveStatValue}>{Number(leaveCounts.remaining_days || 0).toFixed(1)}</div>
              </div>
            </div>

            {leaveByType.length > 0 && (
              <div className={styles.leaveTypeTable}>
                <div className={styles.leaveTypeHeader}>
                  <span>Type</span>
                  <span>Available</span>
                  <span>Used</span>
                  <span>Pending</span>
                  <span>Remaining</span>
                </div>
                {leaveByType.map((row) => (
                  <div key={row.leave_type_name} className={styles.leaveTypeRow}>
                    <span>{row.leave_type_name}</span>
                    <span>{Number(row.total_available_days || 0).toFixed(1)}</span>
                    <span>{Number(row.used_days || 0).toFixed(1)}</span>
                    <span>{Number(row.pending_days || 0).toFixed(1)}</span>
                    <span>{Number(row.remaining_days || 0).toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Next 14 Days Schedule */}
          <div className={styles.section}>
            <h2>My Schedule (Next 14 Days)</h2>
            <div className={styles.miniCalendarGrid}>
              {next14CalendarDays.map((day) => {
                const dayBookings = day.bookings;
                const visibleBookings = dayBookings.slice(0, 3);
                const hiddenCount = Math.max(dayBookings.length - 3, 0);

                return (
                  <div
                    key={day.dateKey}
                    className={`${styles.miniCalendarDay} ${dayBookings.length > 0 ? styles.miniCalendarDayBusy : ''}`}
                  >
                    <div className={styles.miniCalendarHeader}>
                      <span className={styles.miniCalendarWeekday}>
                        {day.dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                      <span className={styles.miniCalendarDate}>{formatDate(day.dateKey)}</span>
                    </div>

                    {dayBookings.length > 0 ? (
                      <div className={styles.miniCalendarEntries}>
                        {visibleBookings.map((booking, idx) => (
                          <div key={`${booking.id}-${day.dateKey}-${idx}`} className={styles.miniCalendarEntry}>
                            {booking.is_time_specific ? `${formatTime(booking.start_datetime)} ` : ''}
                            {booking.project_name || booking.milestone_name || booking.booking_type}
                            {' '}({booking.hours_allocated}h)
                          </div>
                        ))}
                        {hiddenCount > 0 && (
                          <div className={styles.miniCalendarMore}>+{hiddenCount} more</div>
                        )}
                      </div>
                    ) : (
                      <div className={styles.miniCalendarEmpty}>No bookings</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* This Week Timesheets */}
          <div className={styles.section}>
            <h2>Timesheets This Week</h2>
            <div className={styles.timesheetSummary}>
              <span>{thisWeekTimesheets.length} entries</span>
              <span>{thisWeekTimesheetHours.toFixed(1)}h logged</span>
            </div>
            <div className={styles.miniCalendarGrid}>
              {workloadWeekDays.map((day) => {
                const dayEntries = day.entries;
                const visibleEntries = dayEntries.slice(0, 3);
                const hiddenCount = Math.max(dayEntries.length - 3, 0);

                return (
                  <div
                    key={day.dateKey}
                    className={`${styles.miniCalendarDay} ${dayEntries.length > 0 ? styles.miniCalendarDayBusy : ''}`}
                  >
                    <div className={styles.miniCalendarHeader}>
                      <span className={styles.miniCalendarWeekday}>
                        {day.dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                      <span className={styles.miniCalendarDate}>{formatDate(day.dateKey)}</span>
                    </div>

                    {dayEntries.length > 0 ? (
                      <div className={styles.miniCalendarEntries}>
                        {visibleEntries.map((entry) => (
                          <div key={entry.timesheet_id} className={`${styles.miniCalendarEntry} ${styles.miniCalendarEntryTimesheet}`}>
                            {entry.project_name || entry.milestone_name || 'Timesheet'} ({Number(entry.hours_worked || 0).toFixed(1)}h)
                          </div>
                        ))}
                        {hiddenCount > 0 && (
                          <div className={styles.miniCalendarMore}>+{hiddenCount} more</div>
                        )}
                      </div>
                    ) : (
                      <div className={styles.miniCalendarEmpty}>No entries</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Upcoming Deadlines */}
          <div className={styles.section}>
            <h2>My Upcoming Deadlines</h2>
            {personalData.upcomingDeadlines.length > 0 ? (
              <div className={styles.deadlinesList}>
                {personalData.upcomingDeadlines.map((deadline) => (
                  <div key={deadline.milestone_id} className={styles.deadlineItem}>
                    <div className={styles.deadlineDate}>{formatDate(deadline.due_date)}</div>
                    <div className={styles.deadlineContent}>
                      <div className={styles.deadlineName}>{deadline.milestone_name}</div>
                      <div className={styles.deadlineProject}>{deadline.project_name}</div>
                      <span className={`${styles.statusBadge} ${styles[deadline.status]}`}>
                        {deadline.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>No upcoming deadlines</div>
            )}
          </div>

          {/* Assigned Projects */}
          <div className={styles.section}>
            <h2>My Active Projects</h2>
            {personalData.assignedProjects.length > 0 ? (
              <div className={styles.projectGrid}>
                {personalData.assignedProjects.map((project) => (
                  <div 
                    key={project.project_id} 
                    className={styles.projectCard}
                    onClick={() => navigate('/dashboard/projects', { state: { openProjectDetailModal: project.project_id } })}
                  >
                    <div className={styles.projectName}>{project.client_company} - {project.project_name}</div>
                    <div className={styles.projectMeta}>
                      <span className={styles.roleBadge}>{project.role}</span>
                      <span className={`${styles.statusBadge} ${styles[project.status]}`}>
                        {project.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>No active project assignments</div>
            )}
          </div>
              </div>
            )
          )}
        </>
      )}

      {/* 6. Quick Actions Bar */}
      <div className={styles.quickActions}>
        <h3>Quick Actions</h3>
        <div className={styles.actionButtons}>
          {canCreateProjects && (
            <button 
              className={styles.actionBtn}
              onClick={() => navigate('/dashboard/projects', { state: { openNewProjectModal: true } })}
            >
              + New Project
            </button>
          )}
          {canCreateBookings && (
            <button 
              className={styles.actionBtn}
              onClick={() => navigate('/dashboard/schedule')}
            >
              + Assign Resource
            </button>
          )}
          {canCreateTimesheets && (
            <button 
              className={styles.actionBtn}
              onClick={() => navigate('/dashboard/timesheets')}
            >
              + Log Time
            </button>
          )}
          {canCreateOwnTimeOff && (
            <button 
              className={styles.actionBtn}
              onClick={openTimeOffModal}
            >
              {getTimeOffButtonText()}
            </button>
          )}
        </div>
      </div>

      {/* Time Off Request Modal */}
      {showTimeOffModal && (
        <FormModal
          title={canApproveTimeOff ? "Schedule Leave" : "Request Leave"}
          fields={[
            {
              key: 'employee_id',
              label: 'Person/Resource',
              type: 'select',
              required: true,
              value: timeOffForm.employee_id,
              onChange: (e) => setTimeOffForm(prev => ({ ...prev, employee_id: e.target.value })),
              options: canApproveTimeOff 
                ? [
                    { value: "", label: "Select employee..." },
                    ...employees.map(emp => ({
                      value: emp.user_id,
                      label: emp.name + (emp.job_title ? ` (${emp.job_title})` : "")
                    }))
                  ]
                : [{ value: user.user_id, label: user.name + (user.job_title ? ` (${user.job_title})` : "") }],
            },
            {
              key: 'time_off_type_id',
              label: 'Leave Type',
              type: 'select',
              required: true,
              value: timeOffForm.time_off_type_id,
              onChange: (e) => setTimeOffForm(prev => ({ ...prev, time_off_type_id: e.target.value })),
              options: (() => {
                const opts = timeOffTypes.map(type => {
                  return {
                    value: String(type.leave_type_id || type.id || 'unknown'),
                    label: type.name || 'Unnamed'
                  };
                });
                return opts;
              })()
            },
            {
              key: 'dates',
              label: 'Leave Dates',
              type: 'custom',
              render: () => (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
                    <input
                      type="date"
                      required
                      value={timeOffForm.start_date}
                      onChange={(e) => setTimeOffForm(prev => ({ ...prev, start_date: e.target.value }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
                    <input
                      type="date"
                      required
                      value={timeOffForm.end_date}
                      onChange={(e) => setTimeOffForm(prev => ({ ...prev, end_date: e.target.value }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              )
            },
            {
              key: 'reason',
              label: 'Details (Optional)',
              type: 'textarea',
              value: timeOffForm.reason,
              onChange: (e) => setTimeOffForm(prev => ({ ...prev, reason: e.target.value })),
              placeholder: 'Please provide a reason for your leave request...'
            }
          ]}
          onSubmit={handleTimeOffSubmit}
          onCancel={() => setShowTimeOffModal(false)}
          error={timeOffError}
        />
      )}
    </div>
  );
}
